<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Navigation Drawer - Masqué sur les pages fullscreen -->
    <div v-if="!isFullScreenPage" class="flex h-screen overflow-hidden">
      <!-- Sidebar -->
      <Sidebar
        :is-open="drawer"
        :collapsed="sidebarCollapsed"
        :menu-items="menuItems"
        :user-name="userName"
        :user-role="userRole"
        :user-avatar="userAvatar"
        @toggle-collapse="toggleSidebarCollapse"
        @logout="logout"
      />

      <!-- Overlay pour mobile -->
      <div v-if="drawer" @click="drawer = false" class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"></div>

      <!-- Main Content -->
      <main 
        class="w-full flex-1 flex flex-col min-h-screen transition-all duration-300 overflow-hidden"
      >
        <!-- App Bar -->
        <AppHeader
          :breadcrumbs="breadcrumbs"
          :show-quick-actions="showQuickActions"
          :show-notifications="showNotifications"
          :notification-count="notifications"
          :notifications="notificationList"
          @toggle-mobile-menu="drawer = !drawer"
          @toggle-quick-actions="showQuickActions = !showQuickActions"
          @toggle-notifications="showNotifications = !showNotifications"
          @quick-action="quickAction"
          @view-all-notifications="viewAllNotifications"
        />

        <!-- Page Content -->
        <div class="flex-1 p-4 sm:p-6 overflow-auto">
          <router-view />
        </div>

        <!-- Footer -->
        <footer v-if="!isFullScreenPage" class="border-t border-gray-200 bg-white px-4 sm:px-6 py-3">
          <div class="flex items-center justify-between text-sm text-gray-500">
            <div class="flex items-center space-x-4">
              <span>© {{ new Date().getFullYear() }} RHCOM360</span>
            </div>
            <div class="flex items-center space-x-2">
              <span>Version {{ appVersion }}</span>
            </div>
          </div>
        </footer>
      </main>
    </div>

    <!-- Fullscreen Pages (Login, Pointage, etc.) -->
    <router-view v-else />
    
    <!-- Modal de changement de mot de passe forcé -->
    <ForcePasswordChangeModal
      :is-open="shouldShowModal"
      :password-expires-at="passwordExpiresAt || undefined"
      :is-first-login="isFirstLogin"
      @close="closeModal"
      @success="handlePasswordChangeSuccess"
    />

    <!-- Floating Guide Button -->
    <FloatingGuideButton v-if="!isFullScreenPage" @open="showGuide = true" />

    <!-- Platform Guide Modal -->
    <PlatformGuideModal :is-open="showGuide" version="v1" @close="showGuide = false" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useForcePasswordChange } from '@/composables/useForcePasswordChange'
import ForcePasswordChangeModal from '@/components/ForcePasswordChangeModal.vue'
import { useNotificationStore } from '@/stores/notification.store'
import { useMenuPermissions } from '@/composables/useMenuPermissions'
import Sidebar from './Sidebar/Sidebar.vue'
import AppHeader from './Header/AppHeader.vue'
import FloatingGuideButton from '@/components/FloatingGuideButton.vue'
import PlatformGuideModal from '@/components/PlatformGuideModal.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { visibleMenuItems } = useMenuPermissions()

// Gestion du changement de mot de passe forcé
const {
  passwordExpiresAt,
  isFirstLogin,
  shouldShowModal,
  closeModal,
  handlePasswordChangeSuccess,
  initializePasswordCheck
} = useForcePasswordChange()

// État de la navigation
const drawer = ref(true)
const sidebarCollapsed = ref(false)
const showQuickActions = ref(false)
const showNotifications = ref(false)
const showGuide = ref(false)

// Computed properties pour les notifications
const notifications = computed(() => notificationStore.unreadCount)
const notificationList = computed(() => {
  // Prendre les 3 dernières notifications non lues
  const unreadNotifications = notificationStore.unreadNotifications
  
  // Vérification robuste que c'est bien un tableau
  if (!Array.isArray(unreadNotifications)) {
    console.warn('unreadNotifications n\'est pas un tableau:', unreadNotifications)
    return []
  }
  
  return unreadNotifications.slice(0, 3).map(notification => ({
    id: notification._id,
    title: notification.title,
    message: notification.message,
    time: formatNotificationTime(notification.createdAt),
    type: notification.type
  })) as Array<{
    id: string
    title: string
    message: string
    time: string
    type?: string
  }>
})

// Fonction pour formater le temps des notifications
const formatNotificationTime = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  const diffMinutes = Math.ceil(diffTime / (1000 * 60))
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffMinutes < 60) {
    return diffMinutes === 1 ? 'Il y a 1 minute' : `Il y a ${diffMinutes} minutes`
  } else if (diffHours < 24) {
    return diffHours === 1 ? 'Il y a 1 heure' : `Il y a ${diffHours} heures`
  } else if (diffDays < 7) {
    return diffDays === 1 ? 'Il y a 1 jour' : `Il y a ${diffDays} jours`
  } else {
    return date.toLocaleDateString('fr-FR')
  }
}

// Computed pour détecter si on est sur une page sans navigation
const isLoginPage = computed(() => route.path === '/login')
const isPointagePage = computed(() => route.path === '/pointage')
const isForgotPasswordPage = computed(() => route.path === '/forgot-password')
const isResetPasswordPage = computed(() => route.path.startsWith('/reset-password'))
const isFullScreenPage = computed(() => 
  isLoginPage.value || 
  isPointagePage.value || 
  isForgotPasswordPage.value || 
  isResetPasswordPage.value
)

// Breadcrumbs
const breadcrumbs = computed(() => {
  const path = route.path
  const segments = path.split('/').filter(Boolean)
  
  const titleMap: Record<string, string> = {
    'dashboard': 'Tableau de bord',
    'dashboards': 'Tableaux de bord',
    'employes': 'Employés',
    'paie': 'Paie',
    'absences': 'Absences',
    'presence': 'Présence',
    'pointage': 'Pointage',
    'remuneration': 'Rémunération',
    'notes-frais': 'Notes de frais',
    'syndicats': 'Syndicats',
    'equipements': 'Équipements',
    'configuration': 'Configuration',
    'utilisateurs': 'Utilisateurs',
    'notifications': 'Notifications'
  }
  
  return segments.map((segment, index) => ({
    title: titleMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1),
    to: '/' + segments.slice(0, index + 1).join('/')
  }))
})

// Menu items - maintenant géré par le composable useMenuPermissions
const menuItems = visibleMenuItems

// Computed pour les informations utilisateur
const userName = computed(() => {
  if (!authStore.user?.nom || !authStore.user?.prenom) {
    return 'Utilisateur'
  }
  return `${authStore.user.nom.charAt(0).toUpperCase()} ${authStore.user.prenom.charAt(0).toUpperCase()}`
})
const userRole = computed(() => authStore.user?.role || 'Admin')
const userAvatar = computed(() => authStore.user?.avatar || '')

// Méthodes
const toggleSidebarCollapse = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

const logout = async () => {
  await authStore.logout()
  router.push('/login')
}

const quickAction = (action: string) => {
  showQuickActions.value = false
  switch (action) {
    case 'new-employee':
      router.push('/employes')
      break
    case 'new-absence':
      router.push('/absences')
      break
    case 'new-expense':
      router.push('/notes-frais')
      break
    case 'new-document':
      router.push('/documents')
      break
    case 'new-dashboard':
      router.push('/dashboards/editor')
      break
  }
}

const viewAllNotifications = () => {
  showNotifications.value = false
  router.push('/notifications')
}

// Version de l'application
const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0'

// Fermer les menus quand on clique ailleurs
const handleClickOutside = (event: Event) => {
  const target = event.target as Element
  if (!target.closest('.relative')) {
    showQuickActions.value = false
    showNotifications.value = false
  }
}

// Référence pour l'intervalle de rafraîchissement
let refreshInterval: NodeJS.Timeout | null = null

// Initialisation au montage du composant
onMounted(async () => {
  authStore.checkAuth()
  document.addEventListener('click', handleClickOutside)
  
  // Charger les notifications non lues pour le header SEULEMENT si l'utilisateur est authentifié
  if (authStore.isAuthenticated) {
    try {
      await notificationStore.fetchUnreadNotifications()
      // S'assurer que unreadNotifications est un tableau
      if (!Array.isArray(notificationStore.unreadNotifications)) {
        notificationStore.unreadNotifications = []
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error)
      // S'assurer que unreadNotifications est un tableau même en cas d'erreur
      notificationStore.unreadNotifications = []
    }
    
    // Vérifier si un changement de mot de passe est requis
    initializePasswordCheck()
    
    // Rafraîchir les notifications toutes les 30 secondes SEULEMENT si l'utilisateur est authentifié
    refreshInterval = setInterval(async () => {
      // Vérifier à nouveau l'authentification avant chaque rafraîchissement
      if (authStore.isAuthenticated) {
        try {
          await notificationStore.fetchUnreadNotifications()
          // S'assurer que unreadNotifications reste un tableau
          if (!Array.isArray(notificationStore.unreadNotifications)) {
            notificationStore.unreadNotifications = []
          }
        } catch (error) {
          console.error('Erreur lors du rafraîchissement des notifications:', error)
          // S'assurer que unreadNotifications reste un tableau même en cas d'erreur
          notificationStore.unreadNotifications = []
        }
      } else if (refreshInterval) {
        // Si l'utilisateur n'est plus authentifié, arrêter l'intervalle
        clearInterval(refreshInterval)
        refreshInterval = null
      }
    }, 30000)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  
  // Nettoyer l'intervalle de rafraîchissement
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>
